import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { getOrCreateAnonymousSessionId } from '@/utils/session';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const moods = [
  { id: 'amazing', emoji: '🤩', label: 'Amazing', support: 'Things feel bright right now.' },
  { id: 'good', emoji: '😊', label: 'Good', support: 'You are feeling pretty steady.' },
  { id: 'okay', emoji: '😐', label: 'Okay', support: 'Neutral still counts as a real feeling.' },
  { id: 'rough', emoji: '😔', label: 'Rough', support: 'A softer day needs softer care.' },
  { id: 'terrible', emoji: '😢', label: 'Terrible', support: 'You do not have to carry this alone.' }
];

const contexts = [
  { id: 'before-party', label: 'Before party' },
  { id: 'during-party', label: 'During party' },
  { id: 'after-party', label: 'After party' },
  { id: 'general', label: 'General life' }
];

const reflectionPrompts = [
  'What is my body asking for right now?',
  'What helped me feel safer today?',
  'What would make tonight gentler?'
];

const aftercareCards = [
  {
    id: 'aftercare',
    emoji: '🫂',
    title: 'Aftercare & grounding',
    description: 'Water + food, sleep, shower, and checking in with yourself or someone you trust.'
  },
  {
    id: 'raps',
    emoji: '🥤',
    title: 'A reminder: RAPS',
    description: 'Rest, Aqua, Protein, Sugar — basic physical aftercare when you feel wrung out.'
  },
  {
    id: 'grounding',
    emoji: '🌿',
    title: 'Come back to your body',
    description: 'Slow breathing, something warm, something real, and “I’m here, I’m safe, this will pass.”'
  }
];

const pauseSupportPrompts = [
  {
    id: 'need_a_pause',
    title: 'Need a pause?',
    description: 'Ask Nad to help you slow things down and check in.'
  },
  {
    id: 'ground_me',
    title: 'Ground me',
    description: 'Get a simple grounding message you can follow right now.'
  },
  {
    id: 'aftercare_plan',
    title: 'Make an aftercare plan',
    description: 'Get a gentle next-steps plan for water, food, rest, and support.'
  }
];

export default function MoodCheckInPage() {
  const navigate = useNavigate();
  const [sessionId] = useState(() => getOrCreateAnonymousSessionId());
  const [selectedMood, setSelectedMood] = useState(null);
  const [selectedContext, setSelectedContext] = useState('general');
  const [energyLevel, setEnergyLevel] = useState(5);
  const [notes, setNotes] = useState('');
  const [recentCheckIns, setRecentCheckIns] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadMoodHistory = async () => {
      try {
        const response = await axios.get(`${API}/mood`, {
          params: { session_id: sessionId }
        });
        setRecentCheckIns(response.data.logs || []);
      } catch (error) {
        console.error('Error loading mood check-ins:', error);
        toast.error('Could not load your recent check-ins right now.');
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadMoodHistory();
  }, [sessionId]);

  const selectedMoodData = useMemo(
    () => moods.find((mood) => mood.id === selectedMood) || null,
    [selectedMood]
  );

  const moodInsights = useMemo(() => {
    if (recentCheckIns.length === 0) {
      return {
        averageEnergy: '—',
        mostCommonMood: 'No trend yet',
        trendLabel: 'Start logging to spot your patterns.',
        recoveryNudge: 'After a few check-ins, PeeR+ will show your recovery trends here.'
      };
    }

    const totalEnergy = recentCheckIns.reduce((sum, log) => sum + (log.energy_level || 0), 0);
    const moodCounts = recentCheckIns.reduce((accumulator, log) => {
      accumulator[log.mood] = (accumulator[log.mood] || 0) + 1;
      return accumulator;
    }, {});
    const mostCommonMoodId = Object.entries(moodCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'okay';
    const mostCommonMood = moods.find((mood) => mood.id === mostCommonMoodId)?.label || 'Okay';

    const newestSlice = recentCheckIns.slice(0, 3);
    const olderSlice = recentCheckIns.slice(3, 6);
    const newestAverage = newestSlice.reduce((sum, log) => sum + (log.energy_level || 0), 0) / newestSlice.length;
    const olderAverage = olderSlice.length
      ? olderSlice.reduce((sum, log) => sum + (log.energy_level || 0), 0) / olderSlice.length
      : newestAverage;
    const trendDelta = newestAverage - olderAverage;
    const trendLabel = trendDelta > 0.5
      ? 'Energy looks a little better lately.'
      : trendDelta < -0.5
        ? 'Energy looks a bit lower lately.'
        : 'Your energy looks fairly steady lately.';

    const roughCount = recentCheckIns.filter((log) => ['rough', 'terrible'].includes(log.mood)).length;
    const recoveryNudge = roughCount >= 2
      ? 'Looks like your system may need softer recovery time — think water, food, rest, and one safe check-in.'
      : 'Keep noticing the small things that help you stay steady before and after a party.';

    return {
      averageEnergy: (totalEnergy / recentCheckIns.length).toFixed(1),
      mostCommonMood,
      trendLabel,
      recoveryNudge
    };
  }, [recentCheckIns]);

  const handleLogMood = async () => {
    if (!selectedMood || isSaving) return;

    setIsSaving(true);
    try {
      const response = await axios.post(`${API}/mood`, {
        session_id: sessionId,
        mood: selectedMood,
        context: selectedContext,
        energy_level: energyLevel,
        notes
      });

      setRecentCheckIns((prev) => [response.data, ...prev].slice(0, 10));
    setSelectedMood(null);
    setSelectedContext('general');
    setEnergyLevel(5);
    setNotes('');
      toast.success('Mood check-in saved. 💜');
    } catch (error) {
      console.error('Error saving mood check-in:', error);
      toast.error('Could not save your mood check-in right now.');
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getMoodEmoji = (moodId) => {
    return moods.find(m => m.id === moodId)?.emoji || '😐';
  };

  return (
    <div className="min-h-screen flex flex-col relative z-10" data-testid="mood-check-in-page">
      <header className="glass-morphism sticky top-0 z-50 border-b">
        <div className="max-w-2xl mx-auto px-6 py-4">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/')}
              data-testid="mood-check-in-back-button"
              className="mr-4 text-[rgb(var(--text-secondary))] hover:text-[rgb(var(--primary))]"
            >
              <ArrowLeft size={24} weight="duotone" />
            </Button>
            <div className="flex items-center space-x-2">
              <span className="text-3xl emoji-bounce">💜</span>
              <h1 className="text-2xl font-bold text-gradient" data-testid="mood-check-in-title">
                Mood Log + Journaling
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-6 py-8 pb-24">
        <section className="mb-8 rounded-[2rem] border border-[rgb(var(--primary))]/20 bg-gradient-to-br from-[rgb(var(--primary))]/18 to-[rgb(var(--secondary))]/10 p-6" data-testid="mood-check-in-hero">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm uppercase tracking-[0.25em] text-[rgb(var(--text-secondary))] mb-3">Check in gently</p>
              <h2 className="text-3xl font-semibold text-[rgb(var(--text-primary))] mb-3">How are you feeling right now?</h2>
              <p className="text-sm sm:text-base text-[rgb(var(--text-secondary))] leading-relaxed max-w-lg">
                Log your mood, energy, and a few notes so you can spot patterns before a night out, during a session, or while coming down.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:min-w-[220px]">
              <div className="rounded-2xl bg-black/10 px-4 py-3" data-testid="mood-check-in-card-history-count">
                <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Saved logs</div>
                <div className="mt-2 text-2xl font-semibold text-[rgb(var(--text-primary))]">{recentCheckIns.length}</div>
              </div>
              <div className="rounded-2xl bg-black/10 px-4 py-3" data-testid="mood-check-in-card-energy">
                <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Energy</div>
                <div className="mt-2 text-2xl font-semibold text-[rgb(var(--text-primary))]">{energyLevel}/10</div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-8 rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5" data-testid="mood-trends-dashboard">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))]" data-testid="mood-trends-title">
                Mood trends + recovery insights
              </h3>
              <p className="text-sm text-[rgb(var(--text-secondary))] mt-1" data-testid="mood-trends-copy">
                A quick look at how your recent check-ins are moving.
              </p>
            </div>
          </div>

          {/* Visual Mood Chart */}
          {recentCheckIns.length > 0 && (
            <div className="mb-5 rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] p-4" data-testid="mood-visual-chart">
              <p className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-3">Last {Math.min(recentCheckIns.length, 7)} check-ins</p>
              <div className="flex items-end justify-between gap-2 h-24">
                {recentCheckIns.slice(0, 7).reverse().map((log, index) => {
                  const moodScore = { amazing: 5, good: 4, okay: 3, rough: 2, terrible: 1 }[log.mood] || 3;
                  const heightPercent = (moodScore / 5) * 100;
                  const moodColors = {
                    amazing: 'from-emerald-400 to-green-500',
                    good: 'from-cyan-400 to-blue-500',
                    okay: 'from-yellow-400 to-amber-500',
                    rough: 'from-orange-400 to-red-500',
                    terrible: 'from-red-500 to-rose-600'
                  };
                  return (
                    <div key={log.id || index} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-lg">{getMoodEmoji(log.mood)}</span>
                      <div
                        className={`w-full rounded-t-lg bg-gradient-to-t ${moodColors[log.mood] || 'from-gray-400 to-gray-500'} transition-all duration-500`}
                        style={{ height: `${heightPercent}%`, minHeight: '8px' }}
                        title={`${log.mood} - Energy: ${log.energy_level}/10`}
                      />
                      <span className="text-[10px] text-[rgb(var(--text-secondary))]">
                        {new Date(log.timestamp).toLocaleDateString('en-US', { weekday: 'short' }).slice(0, 2)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-[10px] text-[rgb(var(--text-secondary))]">
                <span>Older</span>
                <span>Recent</span>
              </div>
            </div>
          )}

          {/* Energy Trend Line */}
          {recentCheckIns.length > 1 && (
            <div className="mb-5 rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] p-4" data-testid="energy-trend-chart">
              <p className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))] mb-3">Energy levels</p>
              <div className="flex items-end justify-between gap-1 h-16">
                {recentCheckIns.slice(0, 7).reverse().map((log, index) => {
                  const heightPercent = ((log.energy_level || 5) / 10) * 100;
                  return (
                    <div key={log.id || index} className="flex-1 flex flex-col items-center">
                      <div
                        className="w-full rounded-t bg-gradient-to-t from-[rgb(var(--primary))] to-[rgb(var(--accent))] transition-all duration-500"
                        style={{ height: `${heightPercent}%`, minHeight: '4px' }}
                        title={`Energy: ${log.energy_level}/10`}
                      />
                      <span className="mt-1 text-[10px] text-[rgb(var(--text-secondary))]">{log.energy_level}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-3 mb-4">
            <div className="rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] px-4 py-4" data-testid="mood-trends-average-energy-card">
              <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Average energy</div>
              <div className="mt-2 text-2xl font-semibold text-[rgb(var(--text-primary))]">{moodInsights.averageEnergy}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] px-4 py-4" data-testid="mood-trends-common-mood-card">
              <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Most common mood</div>
              <div className="mt-2 text-2xl font-semibold text-[rgb(var(--text-primary))]">{moodInsights.mostCommonMood}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[rgb(var(--surface-elevated))] px-4 py-4" data-testid="mood-trends-shift-card">
              <div className="text-xs uppercase tracking-wider text-[rgb(var(--text-secondary))]">Recent shift</div>
              <div className="mt-2 text-base font-semibold text-[rgb(var(--text-primary))]">{moodInsights.trendLabel}</div>
            </div>
          </div>
          <div className="rounded-2xl border border-[rgb(var(--primary))]/20 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 px-4 py-4" data-testid="mood-trends-recovery-nudge">
            <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">{moodInsights.recoveryNudge}</p>
          </div>
        </section>

        <section className="mb-8" data-testid="mood-selector-section">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {moods.map((mood) => (
              <button
                key={mood.id}
                onClick={() => setSelectedMood(mood.id)}
                data-testid={`mood-option-${mood.id}`}
                className={`flex flex-col items-center p-4 rounded-2xl border-2 transition-all ${
                  selectedMood === mood.id
                    ? 'border-[rgb(var(--primary))] bg-[rgb(var(--primary))]/20 scale-[1.02]'
                    : 'border-white/10 bg-[rgb(var(--surface))] hover:border-[rgb(var(--primary))]/50'
                }`}
              >
                <span className="text-4xl mb-2">{mood.emoji}</span>
                <span className={`text-xs font-medium ${selectedMood === mood.id ? 'text-[rgb(var(--primary))]' : 'text-[rgb(var(--text-secondary))]'}`}>
                  {mood.label}
                </span>
              </button>
            ))}
          </div>
          <div className="mt-4 min-h-[52px] rounded-2xl border border-white/10 bg-[rgb(var(--surface))] px-4 py-3 text-sm text-[rgb(var(--text-secondary))]" data-testid="selected-mood-support-copy">
            {selectedMoodData ? selectedMoodData.support : 'Pick a mood first — no pressure, just honesty.'}
          </div>
        </section>

        <section className="mb-8" data-testid="mood-context-section">
          <p className="text-sm text-[rgb(var(--text-secondary))] mb-3">Context</p>
          <div className="flex flex-wrap gap-2">
            {contexts.map((ctx) => (
              <button
                key={ctx.id}
                onClick={() => setSelectedContext(ctx.id)}
                data-testid={`context-option-${ctx.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedContext === ctx.id
                    ? 'bg-[rgb(var(--primary))] text-white'
                    : 'bg-[rgb(var(--surface))] text-[rgb(var(--text-secondary))] border border-white/10 hover:border-[rgb(var(--primary))]/50'
                }`}
              >
                {ctx.label}
              </button>
            ))}
          </div>
        </section>

        <section className="mb-8" data-testid="mood-energy-section">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-[rgb(var(--text-secondary))]">Energy level</p>
            <span className="text-lg font-bold text-[rgb(var(--primary))]" data-testid="energy-level-value">{energyLevel}/10</span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            value={energyLevel}
            onChange={(e) => setEnergyLevel(parseInt(e.target.value))}
            data-testid="mood-energy-slider"
            className="w-full h-3 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, rgb(var(--primary)) 0%, rgb(var(--primary)) ${(energyLevel - 1) * 11.1}%, rgb(var(--surface)) ${(energyLevel - 1) * 11.1}%, rgb(var(--surface)) 100%)`
            }}
          />
        </section>

        <section className="mb-8" data-testid="mood-notes-section">
          <div className="flex flex-wrap gap-2 mb-3">
            {reflectionPrompts.map((prompt, index) => (
              <button
                key={prompt}
                type="button"
                onClick={() => setNotes((currentNotes) => currentNotes ? `${currentNotes}\n${prompt}` : prompt)}
                data-testid={`reflection-prompt-${index}`}
                className="rounded-full border border-white/10 bg-[rgb(var(--surface))] px-3 py-2 text-xs text-[rgb(var(--text-secondary))] transition-colors hover:border-[rgb(var(--primary))]/40 hover:text-[rgb(var(--text-primary))]"
              >
                {prompt}
              </button>
            ))}
          </div>
          <textarea
            placeholder="Anything you want to remember? Body feelings, cravings, wins, fears, boundaries, or what you need next."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid="mood-notes-input"
            rows={5}
            className="w-full p-4 bg-[rgb(var(--surface))] border border-white/10 rounded-2xl text-[rgb(var(--text-primary))] placeholder:text-[rgb(var(--text-secondary))] focus:outline-none focus:border-[rgb(var(--primary))]/50 resize-none"
          />
          <div className="mt-2 text-right text-xs text-[rgb(var(--text-secondary))]" data-testid="mood-notes-count">
            {notes.length}/1200
          </div>
        </section>

        <Button
          onClick={handleLogMood}
          disabled={!selectedMood || isSaving}
          data-testid="save-mood-check-in-button"
          className="w-full min-h-[56px] rounded-full party-gradient text-white font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving your check-in...' : 'Save mood check-in'}
        </Button>

        <section className="mt-8 rounded-[2rem] border border-[rgb(var(--primary))]/20 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 p-5" data-testid="mood-aftercare-cards-section">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))]" data-testid="mood-aftercare-cards-title">
                Quick aftercare reminders
              </h3>
              <p className="text-sm text-[rgb(var(--text-secondary))] mt-1" data-testid="mood-aftercare-cards-copy">
                Easy things to remember when your body or brain needs softer care.
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/post-party-care')}
              data-testid="mood-open-post-party-care-button"
              className="rounded-full border-[rgb(var(--primary))]/40 text-[rgb(var(--primary))] hover:bg-[rgb(var(--primary))]/10"
            >
              Open full aftercare guide
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {aftercareCards.map((card) => (
              <article
                key={card.id}
                data-testid={`mood-aftercare-card-${card.id}`}
                className="rounded-3xl border border-white/10 bg-[rgb(var(--surface))]/90 p-4"
              >
                <div className="text-3xl mb-3">{card.emoji}</div>
                <h4 className="text-base font-semibold text-[rgb(var(--text-primary))] mb-2">{card.title}</h4>
                <p className="text-sm text-[rgb(var(--text-secondary))] leading-relaxed">{card.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-8 rounded-[2rem] border border-white/10 bg-[rgb(var(--surface))] p-5" data-testid="need-a-pause-section">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-[rgb(var(--text-primary))]" data-testid="need-a-pause-title">
              Need a pause?
            </h3>
            <p className="text-sm text-[rgb(var(--text-secondary))] mt-1" data-testid="need-a-pause-copy">
              If partying feels like too much right now, Nad can help you slow down without judgment.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            {pauseSupportPrompts.map((prompt) => (
              <button
                key={prompt.id}
                type="button"
                onClick={() => navigate(`/chat?action=${prompt.id}`)}
                data-testid={`need-a-pause-prompt-${prompt.id}`}
                className="rounded-3xl border border-white/10 bg-[rgb(var(--surface-elevated))] p-4 text-left transition-colors hover:border-[rgb(var(--primary))]/40 hover:bg-[rgb(var(--surface-elevated))]/90"
              >
                <div className="text-base font-semibold text-[rgb(var(--text-primary))]">{prompt.title}</div>
                <p className="mt-2 text-sm text-[rgb(var(--text-secondary))] leading-relaxed">{prompt.description}</p>
              </button>
            ))}
          </div>
        </section>

        <section className="mt-10" data-testid="mood-history-section">
          <h3 className="text-sm font-medium text-[rgb(var(--text-secondary))] uppercase tracking-wider mb-4">
            Recent check-ins
          </h3>

          {isLoadingHistory ? (
            <div className="rounded-2xl border border-white/10 bg-[rgb(var(--surface))] px-4 py-5 text-sm text-[rgb(var(--text-secondary))]" data-testid="mood-history-loading">
              Loading your recent check-ins...
            </div>
          ) : recentCheckIns.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 bg-[rgb(var(--surface))]/70 px-4 py-6 text-sm text-[rgb(var(--text-secondary))]" data-testid="mood-history-empty-state">
              No check-ins yet. Start with one honest line and build from there.
            </div>
          ) : (
            <div className="space-y-3">
              {recentCheckIns.map((checkIn) => (
                <article
                  key={checkIn.id}
                  data-testid={`mood-history-item-${checkIn.id}`}
                  className="flex items-start p-4 bg-[rgb(var(--surface))] rounded-2xl border border-white/5 gap-4"
                >
                  <span className="text-3xl">{getMoodEmoji(checkIn.mood)}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-medium text-[rgb(var(--text-primary))] capitalize" data-testid={`mood-history-item-mood-${checkIn.id}`}>
                        {checkIn.mood}
                      </span>
                      <span className="text-xs text-[rgb(var(--text-secondary))]" data-testid={`mood-history-item-time-${checkIn.id}`}>
                        {formatDate(checkIn.timestamp)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center mt-1 gap-2 text-sm text-[rgb(var(--text-secondary))]">
                      <span className="capitalize">{checkIn.context.replace('-', ' ')}</span>
                      <span>•</span>
                      <span>Energy: {checkIn.energy_level}/10</span>
                    </div>
                    {checkIn.notes && (
                      <p className="mt-3 text-sm text-[rgb(var(--text-secondary))] italic whitespace-pre-wrap" data-testid={`mood-history-item-notes-${checkIn.id}`}>
                        “{checkIn.notes}”
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <div className="mt-8 p-4 bg-gradient-to-r from-[rgb(var(--primary))]/10 to-[rgb(var(--secondary))]/10 rounded-2xl border border-[rgb(var(--primary))]/20 text-center" data-testid="mood-supportive-copy">
          <p className="text-sm text-[rgb(var(--text-secondary))]">
            Tracking your mood helps you understand patterns. Be gentle with yourself. 💜
          </p>
        </div>
      </main>
    </div>
  );
}
